import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { CurrencyProvider } from "@/contexts/CurrencyContext";
import { CookieConsentBanner } from "@/components/CookieConsentBanner";

// Eager-load the landing page for best LCP
import Index from "./pages/Index";

// Lazy-load everything else for code splitting
const Auth = lazy(() => import("./pages/Auth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Transactions = lazy(() => import("./pages/Transactions"));
const Savings = lazy(() => import("./pages/Savings"));
const Tasks = lazy(() => import("./pages/Tasks"));
const Investments = lazy(() => import("./pages/Investments"));
const CalendarPage = lazy(() => import("./pages/Calendar"));
const Reports = lazy(() => import("./pages/Reports"));
const Budgets = lazy(() => import("./pages/Budgets"));
const Debts = lazy(() => import("./pages/Debts"));
const Recurring = lazy(() => import("./pages/Recurring"));
const Settings = lazy(() => import("./pages/Settings"));
const Pricing = lazy(() => import("./pages/Pricing"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const Terms = lazy(() => import("./pages/Terms"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const FAQ = lazy(() => import("./pages/FAQ"));
const ThankYou = lazy(() => import("./pages/ThankYou"));
const RegisterSuccess = lazy(() => import("./pages/RegisterSuccess"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const BestOf = lazy(() => import("./pages/BestOf"));
const AdminDashboard = lazy(() => import("./pages/blog/AdminDashboard"));
const PostEditor = lazy(() => import("./pages/blog/PostEditor"));
const Subscribers = lazy(() => import("./pages/blog/Subscribers"));
const NewsletterComposer = lazy(() => import("./pages/blog/NewsletterComposer"));
const NewsletterCampaigns = lazy(() => import("./pages/blog/NewsletterCampaigns"));
const CampaignDetail = lazy(() => import("./pages/blog/CampaignDetail"));
const SeedPosts = lazy(() => import("./pages/blog/SeedPosts"));
const SeoHelper = lazy(() => import("./pages/blog/SeoHelper"));
const Assistant = lazy(() => import("./pages/Assistant"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const SuperAdminOverview = lazy(() => import("./pages/superadmin/Overview"));
const SuperAdminUsers = lazy(() => import("./pages/superadmin/Users"));
const SuperAdminSubscriptions = lazy(() => import("./pages/superadmin/Subscriptions"));
const SuperAdminAnalytics = lazy(() => import("./pages/superadmin/Analytics"));
const SuperAdminInvoices = lazy(() => import("./pages/superadmin/Invoices"));
const SuperAdminPermissions = lazy(() => import("./pages/superadmin/Permissions"));
const SuperAdminOnboarding = lazy(() => import("./pages/superadmin/OnboardingFunnel"));
const SuperAdminLeads = lazy(() => import("./pages/superadmin/LeadManagement"));
const SuperAdminSeo = lazy(() => import("./pages/superadmin/SeoMonitoring"));

// Import PWA components directly (tiny, avoids forwardRef warnings from lazy)
import { InstallPrompt } from "@/components/pwa/InstallPrompt";
import { UpdatePrompt } from "@/components/pwa/UpdatePrompt";
import { OfflineIndicator } from "@/components/pwa/OfflineIndicator";

const queryClient = new QueryClient();

function PageFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="animate-pulse text-muted-foreground">Loading…</div>
    </div>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <OfflineIndicator />
      <InstallPrompt />
      <UpdatePrompt />
      <BrowserRouter>
        <AuthProvider>
          <CurrencyProvider>
            <CookieConsentBanner />
            <Suspense fallback={<PageFallback />}>
              <Routes>
                {/* ===== Public pages ===== */}
                <Route path="/" element={<Index />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/cookie-policy" element={<CookiePolicy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/refund-policy" element={<RefundPolicy />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/thank-you" element={<ThankYou />} />
                <Route path="/register-success" element={<RegisterSuccess />} />
                <Route path="/unsubscribe" element={<Unsubscribe />} />
                {/* ===== Auth ===== */}
                <Route path="/auth" element={<Auth />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                {/* ===== App (authenticated) ===== */}
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/transactions" element={<Transactions />} />
                <Route path="/savings" element={<Savings />} />
                <Route path="/tasks" element={<Tasks />} />
                <Route path="/investments" element={<Investments />} />
                <Route path="/calendar" element={<CalendarPage />} />
                <Route path="/reports" element={<Reports />} />
                <Route path="/budgets" element={<Budgets />} />
                <Route path="/debts" element={<Debts />} />
                <Route path="/recurring" element={<Recurring />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/assistant" element={<Assistant />} />
                <Route path="/assistant/:threadId" element={<Assistant />} />
                {/* ===== Super Admin ===== */}
                <Route path="/super-admin" element={<SuperAdminOverview />} />
                <Route path="/super-admin/users" element={<SuperAdminUsers />} />
                <Route path="/super-admin/subscriptions" element={<SuperAdminSubscriptions />} />
                <Route path="/super-admin/analytics" element={<SuperAdminAnalytics />} />
                <Route path="/super-admin/invoices" element={<SuperAdminInvoices />} />
                <Route path="/super-admin/permissions" element={<SuperAdminPermissions />} />
                <Route path="/super-admin/onboarding" element={<SuperAdminOnboarding />} />
                <Route path="/super-admin/leads" element={<SuperAdminLeads />} />
                <Route path="/super-admin/seo" element={<SuperAdminSeo />} />
                {/* ===== Blog ===== */}
                <Route path="/blog" element={<Blog />} />
                <Route path="/best" element={<BestOf />} />
                <Route path="/blog/admin" element={<AdminDashboard />} />
                <Route path="/blog/admin/new" element={<PostEditor />} />
                <Route path="/blog/admin/edit/:id" element={<PostEditor />} />
                <Route path="/blog/admin/subscribers" element={<Subscribers />} />
                <Route path="/blog/admin/newsletter" element={<NewsletterComposer />} />
                <Route path="/blog/admin/campaigns" element={<NewsletterCampaigns />} />
                <Route path="/blog/admin/campaigns/:id" element={<CampaignDetail />} />
                <Route path="/blog/admin/seed" element={<SeedPosts />} />
                <Route path="/blog/admin/seo-helper" element={<SeoHelper />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                {/* ===== Catch-all ===== */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </CurrencyProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
